import React, { useState } from 'react';
    import { motion, AnimatePresence } from 'framer-motion';
    import { useData } from '../context/DataContext';
    import StudentForm from './StudentForm';
    import SessionModal from './SessionModal';
    import SafeIcon from '../common/SafeIcon';
    import * as FiIcons from 'react-icons/fi';

    const { FiEdit, FiTrash2, FiCalendar, FiSearch, FiUser, FiLoader, FiAlertTriangle, FiFilter } = FiIcons;

    const StudentList = () => {
      const { students, deleteStudent, getParentById, formatCurrency, calculateRemainingBalance, statuses } = useData();
      const [editingStudent, setEditingStudent] = useState(null);
      const [sessionStudent, setSessionStudent] = useState(null);
      const [searchTerm, setSearchTerm] = useState('');
      const [statusFilter, setStatusFilter] = useState('all');
      const [confirmDeleteId, setConfirmDeleteId] = useState(null);
      const [isDeleting, setIsDeleting] = useState(false);

      const handleDeleteClick = (id) => {
        if (confirmDeleteId === id) {
          executeDelete(id);
        } else {
          setConfirmDeleteId(id);
          setTimeout(() => setConfirmDeleteId(null), 4000);
        }
      };

      const executeDelete = async (id) => {
        setIsDeleting(true);
        try {
          const res = await deleteStudent(id);
          if (!res.success) throw new Error(res.error);
          setConfirmDeleteId(null);
        } catch (err) {
          alert("Could not delete from Firestore: " + err.message);
        } finally {
          setIsDeleting(false);
        }
      };

      const filteredStudents = students.filter(s => {
        const matchesSearch = s.studentName.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus = statusFilter === 'all' || s.status === statusFilter;
        return matchesSearch && matchesStatus;
      });

      return (
        <div className="space-y-8">
          <div className="bg-white p-4 rounded-[2.5rem] shadow-sm border border-gray-100 flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <SafeIcon icon={FiSearch} className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
              <input 
                type="text" 
                placeholder="Search by student name..." 
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)} 
                className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent rounded-[1.8rem] focus:bg-white focus:border-indigo-600 outline-none transition-all font-bold text-gray-700" 
              />
            </div>
            <div className="relative md:w-64">
              <SafeIcon icon={FiFilter} className="absolute left-6 top-1/2 -translate-y-1/2 text-gray-400" />
              <select 
                value={statusFilter} 
                onChange={(e) => setStatusFilter(e.target.value)} 
                className="w-full pl-14 pr-6 py-4 bg-gray-50 border-2 border-transparent rounded-[1.8rem] focus:bg-white focus:border-indigo-600 outline-none transition-all font-bold text-gray-700 appearance-none cursor-pointer"
              >
                <option value="all">All Statuses</option>
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <AnimatePresence mode='popLayout'>
              {filteredStudents.map((student) => {
                const parent = getParentById(student.parentId);
                const isConfirming = confirmDeleteId === student.id;
                
                return (
                  <motion.div 
                    layout 
                    initial={{ opacity: 0, y: 20 }} 
                    animate={{ opacity: 1, y: 0 }} 
                    exit={{ opacity: 0, scale: 0.9 }} 
                    key={student.id} 
                    className="bg-white rounded-[2.5rem] p-8 shadow-sm border-2 border-transparent hover:border-indigo-600/10 hover:shadow-2xl hover:shadow-indigo-100 transition-all group relative overflow-hidden"
                  >
                    {isDeleting && isConfirming && (
                      <div className="absolute inset-0 bg-white/90 backdrop-blur-md z-30 flex flex-col items-center justify-center">
                        <SafeIcon icon={FiLoader} className="text-4xl text-indigo-600 animate-spin mb-4" />
                        <span className="text-[10px] font-black text-gray-900 uppercase tracking-widest">Processing Cloud Data...</span>
                      </div>
                    )}
                    
                    <div className="flex justify-between items-start mb-8">
                      <div className="w-16 h-16 bg-indigo-50 rounded-[1.5rem] flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                        <SafeIcon icon={FiUser} className="text-3xl" />
                      </div>
                      <span className={`text-[10px] font-black px-4 py-1.5 rounded-full tracking-widest uppercase ${
                        student.status === 'Active' ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {student.status}
                      </span>
                    </div>

                    <h3 className="text-2xl font-black text-gray-900 mb-1 tracking-tight">{student.studentName}</h3>
                    <p className="text-xs font-bold text-gray-400 mb-8 uppercase tracking-widest">
                      Grade {student.grade} • {parent ? `${parent.firstName} ${parent.lastName}` : 'No Parent'}
                    </p>

                    <div className="bg-gray-50 rounded-[1.5rem] p-5 mb-8 flex justify-between items-center">
                      <div className="space-y-0.5">
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Outstanding</p>
                        <p className="text-xl font-black text-rose-500">{formatCurrency(calculateRemainingBalance(student.id))}</p>
                      </div>
                      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shadow-sm text-indigo-600">
                        <SafeIcon icon={FiCalendar} />
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <button 
                        onClick={() => setSessionStudent(student)} 
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white py-3.5 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 shadow-lg shadow-indigo-100"
                      >
                        Sessions
                      </button>
                      <button onClick={() => setEditingStudent(student)} className="p-3.5 bg-gray-50 text-gray-400 rounded-2xl hover:bg-indigo-50 hover:text-indigo-600 transition-all border border-transparent hover:border-indigo-100">
                        <SafeIcon icon={FiEdit} />
                      </button>
                      <button 
                        onClick={() => handleDeleteClick(student.id)} 
                        className={`p-3.5 rounded-2xl transition-all flex items-center gap-2 ${
                          isConfirming ? 'bg-rose-600 text-white px-6' : 'bg-rose-50 text-rose-300 hover:bg-rose-100'
                        }`}
                      >
                        <SafeIcon icon={isConfirming ? FiAlertTriangle : FiTrash2} />
                        {isConfirming && <span className="text-[10px] font-black uppercase tracking-widest">Confirm</span>}
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          {editingStudent && <StudentForm student={editingStudent} onClose={() => setEditingStudent(null)} onSave={() => setEditingStudent(null)} />}
          {sessionStudent && <SessionModal student={sessionStudent} onClose={() => setSessionStudent(null)} />}
        </div>
      );
    };

    export default StudentList;